import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useDms from '../../hooks/useDms';
import { Loading, ErrorBox } from './shared';
import { nfmt, money, formatDate } from '../../lib/formatters';

const priceCell = (v) => (v == null ? '—' : money(v, '$'));
const pctCell = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`);
const tsIso = (u) => (u ? new Date(u * 1000).toISOString() : null);

function SectionsTable({ sections }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Section</th>
          <th>Tier</th>
          <th>Total seats</th>
          <th>Available</th>
          <th>Sold</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((s, i) => (
          <tr key={i}>
            <td>{s.section}</td>
            <td>{s.tier}</td>
            <td>{nfmt(s.total_seats)}</td>
            <td>{nfmt(s.available)}</td>
            <td>{nfmt(s.sold)}</td>
            <td>{s.sell_through_pct}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const pinIcon = L.divIcon({
  className: '',
  html:
    '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 24 32"><path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z" fill="#00dc49" stroke="#ffffff" stroke-width="1.5"/><circle cx="12" cy="12" r="4.5" fill="#ffffff"/></svg>',
  iconSize: [28, 36],
  iconAnchor: [14, 36],
  popupAnchor: [0, -34],
});

function VenueMap({ lat, lng, name }) {
  if (lat == null || lng == null) {
    return <div className="muted">No coordinates available.</div>;
  }
  return (
    <div className="rp-map">
      <MapContainer center={[lat, lng]} zoom={15} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution="Tiles &copy; Esri"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          maxZoom={16}
        />
        <Marker position={[lat, lng]} icon={pinIcon}>
          <Popup>{name || 'Venue'}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}

function EventPanel({ event }) {
  const kv = (k, v) => (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v">{v == null || v === '' ? '—' : v}</span>
    </div>
  );
  return (
    <div className="rp-grid rp-cols-2">
      <div className="rp-card">
        <div className="section-title" style={{ marginTop: 0 }}>
          <h2>Event</h2>
        </div>
        <div className="kv-list">
          {kv('Event', event.event_name)}
          {kv('Performer', event.performer)}
          {kv('Tour', event.tour_name)}
          {kv('Type', event.event_type)}
          {kv('Date', event.event_date)}
          {kv('Time', event.event_time)}
          {kv('First seen', event.first_seen_date)}
          {kv('Days on SeatData', nfmt(event.days_on_seatdata))}
        </div>
      </div>
      <div className="rp-card">
        <div className="section-title" style={{ marginTop: 0 }}>
          <h2>Venue</h2>
        </div>
        <div className="kv-list">
          {kv('Venue', event.venue_name)}
          {kv('City', event.venue_city)}
          {kv('State', event.venue_state)}
          {kv('Country', event.venue_country)}
        </div>
        <VenueMap lat={event.venue_lat} lng={event.venue_lng} name={event.venue_name} />
      </div>
    </div>
  );
}

function SalesTable({ sales, source, setSource }) {
  const rows = source === 'all' ? sales.rows : sales.rows.filter((s) => s.source === source);
  const shCount = sales.rows.filter((s) => s.source === 'sh').length;
  const vsCount = sales.rows.filter((s) => s.source === 'vs').length;
  return (
    <div>
      <div className="rchips" style={{ marginBottom: 12 }}>
        <button className={`rchip${source === 'all' ? ' on' : ''}`} onClick={() => setSource('all')}>
          All ({nfmt(sales.rows.length)})
        </button>
        <button className={`rchip${source === 'sh' ? ' on' : ''}`} onClick={() => setSource('sh')}>
          SeatGeek · sh ({nfmt(shCount)})
        </button>
        <button className={`rchip${source === 'vs' ? ' on' : ''}`} onClick={() => setSource('vs')}>
          Vivid Seats · vs ({nfmt(vsCount)})
        </button>
      </div>
      <div className="muted" style={{ marginBottom: 8 }}>
        {nfmt(sales.total_count)} observed sales · sources tracked:{' '}
        {sales.sources.map((s) => s.source).join(', ') || '—'}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Sold at</th>
            <th>Source</th>
            <th>Zone</th>
            <th>Section</th>
            <th>Row</th>
            <th>Qty</th>
            <th>Price</th>
            <th>All-in</th>
            <th>Listing</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={i}>
              <td>{formatDate(s.sold_at)}</td>
              <td>{s.source}</td>
              <td>{s.zone}</td>
              <td>{s.section}</td>
              <td>{s.row}</td>
              <td>{nfmt(s.quantity)}</td>
              <td>{priceCell(s.price)}</td>
              <td>{priceCell(s.all_in_price)}</td>
              <td>{s.listing_id == null ? '—' : s.listing_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatsTable({ stats }) {
  const latest = stats.rows[0];
  const zones = latest?.zones || [];
  return (
    <div>
      <div className="rchips" style={{ marginBottom: 12 }}>
        {stats.available_zones.map((z) => (
          <span className="rchip" key={z}>
            Zone: {z}
          </span>
        ))}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Get-in</th>
            <th>Get-in 2+</th>
            <th>Median</th>
            <th>Avg</th>
            <th>Active</th>
            <th>All</th>
            <th>Fill rate</th>
          </tr>
        </thead>
        <tbody>
          {stats.rows.map((p, i) => (
            <tr key={i}>
              <td>{formatDate(p.timestamp)}</td>
              <td>{priceCell(p.get_in)}</td>
              <td>{priceCell(p.get_in_qty2plus)}</td>
              <td>{priceCell(p.median_price)}</td>
              <td>{priceCell(p.avg_price)}</td>
              <td>{nfmt(p.total_listings_active)}</td>
              <td>{nfmt(p.total_listings_all)}</td>
              <td>{pctCell(p.listing_fill_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {zones.length > 0 && (
        <div className="rp-card" style={{ marginTop: 12 }}>
          <div className="section-title" style={{ marginTop: 0 }}>
            <h2>Zone breakdown · latest snapshot</h2>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Get-in</th>
                <th>Get-in 2+</th>
                <th>Median</th>
                <th>Avg</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z, i) => (
                <tr key={i}>
                  <td>{z.zone_name}</td>
                  <td>{priceCell(z.get_in)}</td>
                  <td>{priceCell(z.get_in_qty2plus)}</td>
                  <td>{priceCell(z.median_price)}</td>
                  <td>{priceCell(z.avg_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ListingsTable({ listings }) {
  return (
    <div>
      <div className="muted" style={{ marginBottom: 8 }}>
        {nfmt(listings.active_count)} active listings ({nfmt(listings.total_listings)} total observed)
        {listings.last_refresh ? ` · last refresh ${formatDate(tsIso(listings.last_refresh))}` : ''}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Zone</th>
            <th>Section</th>
            <th>Row</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Listing</th>
          </tr>
        </thead>
        <tbody>
          {listings.rows.map((l, i) => (
            <tr key={i}>
              <td>{l.zone}</td>
              <td>{l.section}</td>
              <td>{l.row}</td>
              <td>{nfmt(l.quantity)}</td>
              <td>{priceCell(l.price)}</td>
              <td>{l.listing_id == null ? '—' : l.listing_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListingsView({ slug }) {
  const { data, loading, error } = useDms(`shows/${encodeURIComponent(slug)}/listings`);
  if (loading) return <Loading label="Loading active listings…" />;
  if (error) return <ErrorBox error={error} />;
  const live = data?.live === true;
  const listings = data?.listings || { rows: [], active_count: 0, total_listings: 0 };
  if (!live) {
    return (
      <div className="rp-card">
        <div className="muted">Listings unavailable{data?.reason ? ` (${data.reason})` : ''}.</div>
      </div>
    );
  }
  return <ListingsTable listings={listings} />;
}

export default function ShowDetail({ name, onBack }) {
  const { data, loading, error } = useDms(`shows/${encodeURIComponent(name)}`);
  const [tab, setTab] = useState('sections');
  const [salesSource, setSalesSource] = useState('all');

  if (loading) {
    return (
      <div className="lyve-intel">
        <Loading label="Loading show record…" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="lyve-intel">
        <ErrorBox error={error} />
      </div>
    );
  }

  const r = data?.record || {};
  const artist = r.artist || {};
  const tour = r.tour || {};
  const venue = r.venue || {};
  const show = r.show || {};
  const sales = r.sales || {};
  const tiers = r.tiers || [];
  const signals = r.signals || {};
  const quality = r.quality || {};
  const sections = data?.sections || [];

  const seatdata = data?.seatdata || {};
  const sdLive = seatdata.live === true;
  const sdEvent = seatdata.event || {};
  const sdSales = seatdata.sales || { rows: [], total_count: 0, sources: [] };
  const sdStats = seatdata.stats || { rows: [], total_count: 0, available_zones: [] };

  const stats = [
    { label: 'Capacity', value: nfmt(venue.configured_capacity_e) },
    { label: 'Sold', value: nfmt(sales.tickets_sold_e) },
    { label: 'Available', value: nfmt(sales.tickets_available) },
    { label: '% Sold', value: sales.pct_sold_e == null ? '—' : `${sales.pct_sold_e}%` },
  ];

  const content = (
    <>
      {onBack && (
        <button className="rp-back" onClick={onBack}>
          ‹ Back to shows
        </button>
      )}

      <div className="rp-card" style={{ marginTop: onBack ? 12 : 0, marginBottom: 16 }}>
        <h2 className="wide" style={{ fontSize: 24, margin: 0 }}>
          {artist.name}
        </h2>
        <div className="muted" style={{ marginTop: 4 }}>
          {tour.name} · {venue.name}, {venue.city} · {show.event_date}
        </div>
        <div className="rp-grid rp-cols-4" style={{ marginTop: 16 }}>
          {stats.map((k) => (
            <div className="rp-stat" key={k.label}>
              <div className="label">{k.label}</div>
              <div className="value" style={{ fontSize: 20 }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
        <div className="rchips" style={{ marginTop: 12 }}>
          {signals.demand_read && <span className="rchip">Demand: {signals.demand_read}</span>}
          <span className="rchip">Room: {venue.room_class || '—'}</span>
          {show.general_sale_date && <span className="rchip">On sale {show.general_sale_date}</span>}
        </div>
      </div>

      <div className="rp-split">
        <div className="rp-card">
          <div className="section-title" style={{ marginTop: 0 }}>
            <h2>Tier sell-through</h2>
          </div>
          <div className="tier-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Sections</th>
                  <th>Seats</th>
                  <th>Sold</th>
                  <th>Available</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t, i) => (
                  <tr key={i}>
                    <td>{t.tier}</td>
                    <td>{nfmt(t.sections)}</td>
                    <td>{nfmt(t.seats)}</td>
                    <td>{nfmt(t.sold)}</td>
                    <td>{nfmt(t.available)}</td>
                    <td>{t.sell_through_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rp-card">
          <div className="section-title" style={{ marginTop: 0 }}>
            <h2>Signals & quality</h2>
          </div>
          <div className="kv-list">
            <div className="kv">
              <span className="k">Upper vs lower gap</span>
              <span className="v">{signals.upper_vs_lower_gap_pts ?? '—'} pts</span>
            </div>
            <div className="kv">
              <span className="k">Lower-bowl unsold share</span>
              <span className="v">{signals.lower_bowl_share_of_unsold_pct ?? '—'}%</span>
            </div>
            <div className="kv">
              <span className="k">Sections ≥ 90%</span>
              <span className="v">{nfmt(signals.sections_90_plus)}</span>
            </div>
            <div className="kv">
              <span className="k">Sections &lt; 60%</span>
              <span className="v">{nfmt(signals.sections_below_60)}</span>
            </div>
            <div className="kv">
              <span className="k">Reported sold</span>
              <span className="v">{nfmt(quality.reported_sold)}</span>
            </div>
            <div className="kv">
              <span className="k">Reconciliation gap</span>
              <span className="v">{nfmt(quality.reconciliation_gap)}</span>
            </div>
            <div className="kv">
              <span className="k">Consumers</span>
              <span className="v">{(r.consumers || []).join(', ')}</span>
            </div>
            <div className="kv">
              <span className="k">Uses</span>
              <span className="v">{(r.uses || []).join(', ')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="section-title">
        <h2>SeatData · live resale market</h2>
      </div>

      {!sdLive && (
        <div className="rp-card">
          <div className="muted">
            Live SeatData unavailable{seatdata.reason ? ` (${seatdata.reason})` : ''}. Static section inventory is shown
            below.
          </div>
        </div>
      )}

      <div className="ftabs" style={{ marginTop: 14 }}>
        <button className="ftab" aria-pressed={tab === 'sections'} onClick={() => setTab('sections')}>
          Sections ({sections.length})
        </button>
        {sdLive && (
          <>
            <button className="ftab" aria-pressed={tab === 'event'} onClick={() => setTab('event')}>
              Event
            </button>
            <button className="ftab" aria-pressed={tab === 'sales'} onClick={() => setTab('sales')}>
              Sales ({nfmt(sdSales.total_count)})
            </button>
            <button className="ftab" aria-pressed={tab === 'price'} onClick={() => setTab('price')}>
              Price history ({nfmt(sdStats.total_count)})
            </button>
            <button className="ftab" aria-pressed={tab === 'listings'} onClick={() => setTab('listings')}>
              Listings
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        {tab === 'sections' && <SectionsTable sections={sections} />}
        {sdLive && tab === 'event' && <EventPanel event={sdEvent} />}
        {sdLive && tab === 'sales' && <SalesTable sales={sdSales} source={salesSource} setSource={setSalesSource} />}
        {sdLive && tab === 'price' && <StatsTable stats={sdStats} />}
        {sdLive && tab === 'listings' && <ListingsView slug={name} />}
      </div>
    </>
  );

  if (onBack) {
    return (
      <div className="lyve-intel">
        <div className="page">{content}</div>
      </div>
    );
  }
  return <div className="lyve-intel">{content}</div>;
}
